import { useFloatingScrollbar } from "@tellescope/react-hooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { DAY_COLUMN, LOOKBACK_IN_MS_BY_VIEW } from "../constants";
import { getEventDayLayouts, getEventsByDate, getHiddenEventsFlags } from "../../utils";
import { useCalendarEventsWithFilters, useDayBoundsMinutes } from "../../hooks";
import { FAKE_CALENDAR_USER_ID } from "../../filters";
import type { EventsByDateWithLayout } from "../../types";
import type { CalendarDayViewProps } from "./CalendarDayView";

export const useCalendarDayViewLogic = ({
  referenceDate,
  startDate,
  timezone,
  filters,
  currentUserId,
}: CalendarDayViewProps) => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTwoTimezones = !!timezone && browserTimezone !== timezone;
  const timestampForCalculations = referenceDate ?? startDate;

  const [creationMetadata, setCreationMetadata] = useState<{
    isCreating: boolean;
    initialTimestamp: number;
    initialDurationMinutes?: number;
  }>();
  const [selectedMonth, setSelectedMonth] = useState(referenceDate);

  const handleCreateClick = useCallback((initialTimestamp: number, initialDurationMinutes?: number) => {
    setCreationMetadata({ isCreating: true, initialTimestamp, initialDurationMinutes });
  }, []);

  const selectedUserIds = useMemo(
    () => (filters?.userIds ?? []).filter((id) => id !== FAKE_CALENDAR_USER_ID),
    [filters?.userIds]
  );

  const closeCreation = useCallback(() => setCreationMetadata(undefined), []);

  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate: timestampForCalculations,
    view: "day",
    timezone,
    lookbackInMS: LOOKBACK_IN_MS_BY_VIEW.day,
    filters,
    currentUserId,
  });

  const eventsByDate = useMemo<EventsByDateWithLayout>(() => {
    const grouped = getEventsByDate(eventsQuery.data ?? [], timezone);
    const result: EventsByDateWithLayout = {};
    for (const timestampKey of Object.keys(grouped)) {
      const timestamp = Number(timestampKey);
      const dayEvents = grouped[timestamp] ?? [];
      const layouts = getEventDayLayouts(dayEvents);
      result[timestamp] = dayEvents.map((event) => ({
        ...event,
        layout: layouts.get(event.id) ?? { widthPercent: 100, leftPercent: 0 },
      }));
    }
    return result;
  }, [eventsQuery.data, timezone]);

  const headersRef = useRef<HTMLDivElement>(null);
  const {
    scrollContainerRef,
    thumbRef,
    handleScroll: _handleScroll,
  } = useFloatingScrollbar({ trackInsetTopRef: headersRef });
  const { dayStartMinutes: baseDayStartMinutes, dayEndMinutes: baseDayEndMinutes } = useDayBoundsMinutes();

  const [expandEarlier, setExpandEarlier] = useState(false);
  const [expandLater, setExpandLater] = useState(false);
  const [isNearTop, setIsNearTop] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(false);

  const [prevReferenceTime, setPrevReferenceTime] = useState(referenceDate?.getTime());
  if (referenceDate?.getTime() !== prevReferenceTime) {
    setPrevReferenceTime(referenceDate?.getTime());
    setExpandEarlier(false);
    setExpandLater(false);
    setIsNearTop(false);
    setIsNearBottom(false);
  }

  const handleScroll = useCallback(() => {
    _handleScroll();
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setIsNearTop(scrollTop <= 40);
    setIsNearBottom(scrollHeight - scrollTop - clientHeight <= 40);
  }, [_handleScroll, scrollContainerRef]);

  const dayStartMinutes = expandEarlier ? 0 : baseDayStartMinutes;
  const dayEndMinutes = expandLater ? 24 * 60 : baseDayEndMinutes;

  const { hasEarlierHiddenEvents, hasLaterHiddenEvents } = useMemo(
    () => getHiddenEventsFlags(eventsByDate, dayStartMinutes, dayEndMinutes),
    [eventsByDate, dayStartMinutes, dayEndMinutes]
  );

  const handleExpandEarlier = useCallback(() => {
    const heightAdded = baseDayStartMinutes * DAY_COLUMN.MINUTE_HEIGHT;
    setExpandEarlier(true);
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = heightAdded;
      requestAnimationFrame(() => el.scrollTo({ top: 0, behavior: "smooth" }));
    });
  }, [baseDayStartMinutes, scrollContainerRef]);

  const handleExpandLater = useCallback(() => {
    setExpandLater(true);
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [scrollContainerRef]);

  return {
    browserTimezone,
    showTwoTimezones,
    eventsByDate,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    headersRef,
    creationMetadata,
    handleCreateClick,
    selectedUserIds,
    closeCreation,
    selectedMonth,
    setSelectedMonth,
    dayStartMinutes,
    dayEndMinutes,
    hasEarlierHiddenEvents,
    hasLaterHiddenEvents,
    handleExpandEarlier,
    handleExpandLater,
    isNearTop,
    isNearBottom,
  };
};
